import { DisabledCoachAI, type CoachAIProvider } from '@cadencia/domain';
import { useMemo } from 'react';
import { getSupabase } from '../auth/supabaseClient.js';
import { useAppData } from './DataProvider.js';
import { iaHabilitada, usePreferencias } from './preferenciaIA.js';
import { recordAIVerdict } from './repositories/aiDecisionsRepo.js';
import { SupabaseCoachAI } from './services/coachAI.js';

/**
 * El proveedor de IA que corresponde AHORA MISMO.
 *
 * Devuelve `DisabledCoachAI` —no `null`— cuando la IA no aplica: sin sesion,
 * sin Supabase configurado, o con el interruptor apagado. Asi quien llama
 * nunca tiene que comprobar si hay proveedor: siempre lo hay, y a veces dice
 * que no. Es lo que hace que el camino determinista sea el camino normal y no
 * una rama de excepcion (docs/AI_ARCHITECTURE.md §6).
 *
 * El interruptor se lee de Dexie con `useLiveQuery`, asi que apagarlo en la
 * pantalla de «IA y coste» surte efecto sin recargar.
 */
export function useCoachAI(): CoachAIProvider {
  const { ctx } = useAppData();
  const userId = ctx?.userId ?? null;

  // Sin fila de preferencias, encendida: es el estado de un usuario recien
  // creado, y la Edge Function asume lo mismo por el mismo motivo
  // (`user_preferences.ai_enabled` tiene `default true`).
  //
  // Pero solo cuando ESO SE SABE. En un dispositivo recien estrenado, con la
  // primera bajada todavia en vuelo, «no hay fila» significa «no la he mirado»,
  // y aqui se daba por encendida una IA que el usuario podia tener apagada en
  // el servidor: una llamada que cuesta dinero contra un ajuste explicito. La
  // regla vive entera en `preferenciaIA.ts` (NEW5-002).
  const habilitada = iaHabilitada(usePreferencias(userId));

  return useMemo(() => {
    const supabase = getSupabase();
    if (!supabase || !habilitada || ctx === null) return new DisabledCoachAI();
    // El veredicto se anota desde aqui porque aqui es donde hay contexto de
    // escritura. Sin `ctx` no hay proveedor encendido: una IA que no pudiera
    // registrar lo que el dominio decide sobre ella dejaria `accepted` en nulo
    // para siempre, que es el estado que este parametro existe para evitar.
    return new SupabaseCoachAI(supabase, true, (entrada) => recordAIVerdict(ctx, entrada));
  }, [ctx, habilitada]);
}
